import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';

import AdminLayout from '../layouts/AdminLayout';

const lazyPage = <T extends React.ComponentType<{}>>(
    importer: () => Promise<{ default: T }>
) =>
    React.lazy(() =>
        importer().catch((error) => {
            const message =
                typeof error?.message === 'string' ? error.message : '';
            const isChunkLoadError =
                error?.name === 'ChunkLoadError' ||
                /Loading chunk .* failed/i.test(message);

            if (isChunkLoadError && globalThis.window !== undefined) {
                const cacheKey = 'saca:chunk-load-retry';
                if (!globalThis.window.sessionStorage.getItem(cacheKey)) {
                    globalThis.window.sessionStorage.setItem(cacheKey, 'true');
                    globalThis.window.location.reload();
                    return new Promise(() => {
                        /* pending until reload */
                    });
                }
            }

            throw error;
        })
    );

// Feature pages - lazy loaded for code splitting
const DashboardPage = lazyPage(() => import('../features/dashboard/DashboardPage'));
const AnalyticsPage = lazyPage(() => import('../features/dashboard/AnalyticsPage'));
const UsersListPage = lazyPage(() => import('../features/users/UsersListPage'));
const UserDetailPage = lazyPage(() => import('../features/users/UserDetailPage'));
const OrganizersPage = lazyPage(() => import('../features/users/OrganizersPage'));
const OrganizerDetailPage = lazyPage(() => import('../features/users/OrganizerDetailPage'));
const SacaProPage = lazyPage(() => import('../features/users/SacaProPage'));
const ReferralsPage = lazyPage(() => import('../features/users/ReferralsPage'));
const ReferralRewardsPage = lazyPage(() => import('../features/users/ReferralRewardsPage'));

const GamesListPage = lazyPage(() => import('../features/games/GamesListPage'));
const GameDetailPage = lazyPage(() => import('../features/games/GameDetailPage'));
const GameFormPage = lazyPage(() => import('../features/games/GameFormPage'));
const FreeGamesPage = lazyPage(() => import('../features/games/FreeGamesPage'));
const GameApprovalsPage = lazyPage(() => import('../features/games/GameApprovalsPage'));
const AppointmentsPage = lazyPage(() => import('../features/games/AppointmentsPage'));

const PitchesListPage = lazyPage(() => import('../features/venues/PitchesListPage'));
const PitchDetailPage = lazyPage(() => import('../features/venues/PitchDetailPage'));
const PitchFormPage = lazyPage(() => import('../features/venues/PitchFormPage'));
const PitchApprovalsPage = lazyPage(() => import('../features/venues/PitchApprovalsPage'));
const BookingsPage = lazyPage(() => import('../features/venues/BookingsPage'));

const LeaguesListPage = lazyPage(() => import('../features/competitions/LeaguesListPage'));
const LeagueDetailPage = lazyPage(() => import('../features/competitions/LeagueDetailPage'));
const EventsListPage = lazyPage(() => import('../features/competitions/EventsListPage'));

const TransactionsPage = lazyPage(() => import('../features/finance/TransactionsPage'));
const PlansPage = lazyPage(() => import('../features/finance/PlansPage'));
const PlanFormPage = lazyPage(() => import('../features/finance/PlanFormPage'));
const GamePayoutsPage = lazyPage(() => import('../features/finance/GamePayoutsPage'));

const BlogsListPage = lazyPage(() => import('../features/content/BlogsListPage'));
const BlogEditorPage = lazyPage(() => import('../features/content/BlogEditorPage'));
const CharityPage = lazyPage(() => import('../features/content/CharityPage'));
const EquipmentPage = lazyPage(() => import('../features/content/EquipmentPage'));
const FaqsPage = lazyPage(() => import('../features/content/FaqsPage'));
const TeamsPage = lazyPage(() => import('../features/content/TeamsPage'));
const MediaLibraryPage = lazyPage(() => import('../features/content/MediaLibraryPage'));

const SettingsPage = lazyPage(() => import('../features/settings/SettingsPage'));
const LocationsPage = lazyPage(() => import('../features/settings/LocationsPage'));
const NotificationsPage = lazyPage(() => import('../features/settings/NotificationsPage'));
const ProjectsPage = lazyPage(() => import('../features/settings/ProjectsPage'));
const EmailTemplatesPage = lazyPage(() => import('../features/settings/EmailTemplatesPage'));
const AuditLogsPage = lazyPage(() => import('../features/settings/AuditLogsPage'));
const AdminOrdersPage = lazyPage(() => import('../features/shop/AdminOrdersPage'));
const ProjectDetailPage = lazyPage(() => import('../features/settings/ProjectDetailPage'));

// Loading fallback component
const PageLoader: React.FC = () => (
    <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            <p className="text-sm text-text-muted">Loading...</p>
        </div>
    </div>
);

const LegacyOrganizerDetailRedirect: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={id ? `/admin/organizer-management/${id}` : '/admin/organizer-management'} replace />;
};

const AdminRouter: React.FC = () => {
    return (
        <Routes>
            <Route path="/" element={<AdminLayout />}>
                {/* Dashboard */}
                <Route
                    index
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <DashboardPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="analytics"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <AnalyticsPage />
                        </React.Suspense>
                    }
                />

                {/* Users */}
                <Route
                    path="users"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <UsersListPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="users/:id"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <UserDetailPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="organizer-management"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <OrganizersPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="organizer-management/:id"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <OrganizerDetailPage />
                        </React.Suspense>
                    }
                />
                <Route path="organizers" element={<Navigate to="/admin/organizer-management" replace />} />
                <Route path="organizers/:id" element={<LegacyOrganizerDetailRedirect />} />
                <Route
                    path="saca-pro"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <SacaProPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="referrals"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <ReferralsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="referral-rewards"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <ReferralRewardsPage />
                        </React.Suspense>
                    }
                />

                {/* Games */}
                <Route
                    path="games"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <GamesListPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="games/new"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <GameFormPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="games/:id/edit"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <GameFormPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="games/:id"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <GameDetailPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="free-games"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <FreeGamesPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="game-approvals"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <GameApprovalsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="appointments"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <AppointmentsPage />
                        </React.Suspense>
                    }
                />

                {/* Venues */}
                <Route
                    path="pitches"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <PitchesListPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="pitches/new"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <PitchFormPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="pitches/:id/edit"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <PitchFormPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="pitches/:id"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <PitchDetailPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="bookings"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <BookingsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="pitch-approvals"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <PitchApprovalsPage />
                        </React.Suspense>
                    }
                />

                {/* Competitions */}
                <Route
                    path="leagues"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <LeaguesListPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="leagues/new"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <LeagueDetailPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="leagues/:id"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <LeagueDetailPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="events"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <EventsListPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="events/new"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <LeagueDetailPage />
                        </React.Suspense>
                    }
                />

                {/* Finance */}
                <Route
                    path="transactions"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <TransactionsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="plans"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <PlansPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="plans/new"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <PlanFormPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="plans/:id/edit"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <PlanFormPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="game-payouts"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <GamePayoutsPage />
                        </React.Suspense>
                    }
                />

                {/* Content */}
                <Route
                    path="blogs"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <BlogsListPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="blogs/new"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <BlogEditorPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="blogs/:id"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <BlogEditorPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="charity"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <CharityPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="equipment"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <EquipmentPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="faqs"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <FaqsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="teams"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <TeamsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="media"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <MediaLibraryPage />
                        </React.Suspense>
                    }
                />

                {/* Settings */}
                <Route
                    path="settings"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <SettingsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="locations"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <LocationsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="notifications"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <NotificationsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="projects"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <ProjectsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="projects/:id"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <ProjectDetailPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="email-templates"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <EmailTemplatesPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="audit-logs"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <AuditLogsPage />
                        </React.Suspense>
                    }
                />
                <Route
                    path="orders"
                    element={
                        <React.Suspense fallback={<PageLoader />}>
                            <AdminOrdersPage />
                        </React.Suspense>
                    }
                />

                {/* Catch-all redirect */}
                <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>
        </Routes>
    );
};

export default AdminRouter;
